import { Component, OnInit, ChangeDetectorRef, OnDestroy, ChangeDetectionStrategy, ViewEncapsulation, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, ValidatorFn, AbstractControl, FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { FileUploadService } from '../../services/file-upload.service';
import { UserRegistration } from '../../interfaces/product.interface';
import { ToastService } from '../../services/toast.service';
import { LanguageService } from '../../services/language.service';
import { LanguageSelectorComponent } from '../../components/shared/language-selector/language-selector';
import { OfflinePurchaseModalComponent } from '../../components/shared/offline-purchase-modal/offline-purchase-modal';
import { OfflineDoseEntry } from '../../interfaces/product.interface';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { RecaptchaVerifier, ConfirmationResult } from '@angular/fire/auth';
import { Subscription, combineLatest } from 'rxjs';

@Component({
  selector: 'app-register',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, LanguageSelectorComponent, OfflinePurchaseModalComponent],
  templateUrl: './register.html',
  styleUrl: './register.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class Register implements OnInit, OnDestroy {
  registrationForm: FormGroup;
  otpForm: FormGroup;
  isLoading = false;
  errorMessage = '';
  successMessage = '';
  selectedPhotoFile: File | null = null;
  photoPreview: string | null = null;
  readonly maxPhotoBytes = 2 * 1024 * 1024; // 2 MB limit
  
  // OTP validation state
  showOtpInput = false;
  otpSent = false;
  recaptchaVerifier!: RecaptchaVerifier;
  confirmationResult!: ConfirmationResult;
  countdown = 0;
  countdownInterval: any;
  pendingRegistrationData: UserRegistration | null = null;
  isRegistering = false; // Flag to prevent redirect during registration
  maxDate: string; // Maximum date for date picker (10 years ago - users must be more than 10 years old)
  minDate: string; // Minimum date for date picker (100 years ago - reasonable limit)
  showValidationAnimation = false; // Controls submit error animation on card
  showOfflineModal = false;
  pendingOfflineData: OfflineDoseEntry[] | null = null;
  
  // Subscriptions for cleanup
  private subscriptions = new Subscription();
  private loggedInSubscription?: Subscription;

  ngOnInit(): void {
    // Check if user is already logged in using observable
    this.loggedInSubscription = this.authService.isLoggedIn().subscribe(isLoggedIn => {
      if (isLoggedIn && !this.isRegistering) {
        this.router.navigate(['/home']);
      }
    });
    
    // Subscribe to language and translations changes to ensure UI updates on refresh
    // This is critical for OnPush change detection strategy
    this.subscriptions.add(
      combineLatest([
        this.languageService.currentLanguage$,
        this.languageService.translations$
      ]).subscribe(() => {
        // Trigger change detection when language or translations change
        // This ensures the template updates when translations load after page refresh
        this.cdr.markForCheck();
      })
    );
    
    // Initialize reCAPTCHA
    setTimeout(() => {
      this.initializeRecaptcha();
    }, 100);
  }
  
  ngOnDestroy(): void {
    // Clear countdown interval
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    
    // Unsubscribe from all subscriptions
    this.subscriptions.unsubscribe();
    if (this.loggedInSubscription) {
      this.loggedInSubscription.unsubscribe();
    }
    
    // Clear reCAPTCHA
    if (this.recaptchaVerifier) {
      try {
        this.recaptchaVerifier.clear();
      } catch (error) {
        // Silent cleanup
      }
    }
  }

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private toastService: ToastService,
    private languageService: LanguageService,
    private cdr: ChangeDetectorRef,
    private fileUploadService: FileUploadService,
    private ngZone: NgZone
  ) {
    // Set maximum date to 10 years ago (users must be more than 10 years old)
    const today = new Date();
    const maxDate = new Date();
    maxDate.setFullYear(today.getFullYear() - 10);
    maxDate.setDate(maxDate.getDate() - 1); // Subtract 1 day to ensure more than 10 years old
    this.maxDate = maxDate.toISOString().split('T')[0];
    
    // Set minimum date to 100 years ago (reasonable limit)
    const minDate = new Date();
    minDate.setFullYear(today.getFullYear() - 100);
    this.minDate = minDate.toISOString().split('T')[0];
    this.registrationForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      mobile: ['', [Validators.required, Validators.pattern(/^[6-9]\d{9}$/)]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      gender: ['', [Validators.required]],
      currentWeight: ['', [Validators.required, Validators.min(20), Validators.max(200)]],
      currentHeight: ['', [Validators.required, Validators.min(100), Validators.max(250)]],
      dateOfBirth: ['', [Validators.required, this.minAgeValidator(10)]],
      fullAddress: ['', [Validators.required, Validators.minLength(10)]],
      altMobile: ['', [Validators.pattern(/^[6-9]\d{9}$/)]],
      agreeTerms: [false, [Validators.requiredTrue]],
      alreadyBoughtDose: ['no' as 'yes' | 'no']
    }, { validators: this.passwordMatchValidator });
    
    this.otpForm = this.fb.group({
      otp: ['', [Validators.required, Validators.pattern(/^[0-9]{6}$/)]]
    });
    
    // Add real-time validation for email and mobile (with proper subscription management)
    const emailSubscription = this.registrationForm.get('email')?.valueChanges.pipe(
      debounceTime(500),
      distinctUntilChanged()
    ).subscribe(email => {
      if (email && this.registrationForm.get('email')?.valid) {
        const checkEmailSub = this.authService.checkEmailExists(email).subscribe(exists => {
          if (exists) {
            this.registrationForm.get('email')?.setErrors({ 'emailExists': true });
          }
        });
        this.subscriptions.add(checkEmailSub);
      }
    });
    
    const mobileSubscription = this.registrationForm.get('mobile')?.valueChanges.pipe(
      debounceTime(500),
      distinctUntilChanged()
    ).subscribe(mobile => {
      if (mobile && this.registrationForm.get('mobile')?.valid) {
        const checkMobileSub = this.authService.checkMobileExists(mobile).subscribe(exists => {
          if (exists) {
            this.registrationForm.get('mobile')?.setErrors({ 'mobileExists': true });
          }
        });
        this.subscriptions.add(checkMobileSub);
      }
    });
    
    if (emailSubscription) this.subscriptions.add(emailSubscription);
    if (mobileSubscription) this.subscriptions.add(mobileSubscription);

    const offlineSub = this.registrationForm.get('alreadyBoughtDose')?.valueChanges.subscribe(v => {
      if (v === 'no') this.pendingOfflineData = null;
    });
    if (offlineSub) this.subscriptions.add(offlineSub);
  }

  // Validator to ensure minimum age in years
  private minAgeValidator(minYears: number): ValidatorFn {
    return (control: AbstractControl) => {
      const rawValue = control.value;
      if (!rawValue) {
        return null;
      }
      const dob = new Date(rawValue);
      if (isNaN(dob.getTime())) {
        return null;
      }
      const today = new Date();
      const minDate = new Date(
        today.getFullYear() - minYears,
        today.getMonth(),
        today.getDate()
      );
      // If dob is after the minDate, user is too young
      return dob > minDate ? { minAge: { requiredAge: minYears } } : null;
    };
  }

  passwordMatchValidator(form: FormGroup) {
    const password = form.get('password')?.value;
    const confirmPassword = form.get('confirmPassword')?.value;
    
    if (password !== confirmPassword) {
      form.get('confirmPassword')?.setErrors({ 'mismatch': true });
    } else {
      const errors = form.get('confirmPassword')?.errors;
      if (errors && errors['mismatch']) {
        delete errors['mismatch'];
        if (Object.keys(errors).length === 0) {
          form.get('confirmPassword')?.setErrors(null);
        }
      }
    }
    return null;
  }

  onFileSelected(event: any) {
    event?.stopPropagation?.();
    const input = event?.target || event?.currentTarget;
    const file = input?.files?.[0];
    
    if (!file) {
      if (input) input.value = '';
      return;
    }

    // Validate file size (max 2MB) - immediate feedback
    if (file.size > this.maxPhotoBytes) {
      const kb = Math.round(this.maxPhotoBytes / 1024);
      this.errorMessage = `Image too large. Max allowed size is ${kb >= 1024 ? kb / 1024 + ' MB' : kb + ' KB'}`;
      this.selectedPhotoFile = null;
      this.photoPreview = null;
      if (input) input.value = '';
      this.cdr.detectChanges();
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      this.errorMessage = 'Please select a valid image file';
      this.selectedPhotoFile = null;
      this.photoPreview = null;
      if (input) input.value = '';
      this.cdr.detectChanges();
      return;
    }

    // Set file immediately
    this.selectedPhotoFile = file;
    this.errorMessage = '';
    
    // Create preview immediately using FileReader
    const reader = new FileReader();
    
    reader.onload = (e: any) => {
      const result = e.target?.result;
      if (result) {
        // Run inside Angular zone to ensure change detection
        this.ngZone.run(() => {
          this.photoPreview = result;
          this.cdr.detectChanges();
          
          // Reset input to allow selecting the same file again
          if (input) {
            setTimeout(() => input.value = '', 50);
          }
        });
      }
    };
    
    reader.onerror = () => {
      this.ngZone.run(() => {
        this.errorMessage = 'Failed to load image. Please try again.';
        this.selectedPhotoFile = null;
        this.photoPreview = null;
        if (input) input.value = '';
        this.cdr.detectChanges();
      });
    };
    
    // Start reading the file
    reader.readAsDataURL(file);
  }

  removePhoto() {
    this.selectedPhotoFile = null;
    this.photoPreview = null;
    this.errorMessage = '';
    this.cdr.detectChanges();
  }

  onSubmit() {
    if (!this.showOtpInput) {
      // First step: Send OTP for mobile verification
      this.sendOTPForRegistration();
    } else {
      // Second step: Verify OTP and complete registration
      this.verifyOTPAndRegister();
    }
  }
  
  // Send OTP for mobile verification during registration
  private sendOTPForRegistration(): void {
    // Mark all fields as touched to show validation errors
    this.markFormGroupTouched();
    
    // Check if form is valid
    if (!this.registrationForm.valid) {
      this.triggerValidationAnimation();
      this.scrollToFirstInvalidField();
      this.toastService.error('Please fill all required fields correctly');
      return;
    }
    
    // Check if photo is uploaded
    if (!this.selectedPhotoFile) {
      this.errorMessage = 'Please upload your recent photo';
      this.scrollToPhotoUpload();
      this.toastService.error('Please upload your recent photo');
      this.triggerValidationAnimation();
      return;
    }

    const boughtOffline = this.registrationForm.value.alreadyBoughtDose === 'yes';
    if (boughtOffline && !this.pendingOfflineData) {
      this.showOfflineModal = true;
      this.cdr.detectChanges();
      return;
    }
    
    this.isLoading = true;
    this.errorMessage = '';
    
    const mobile = this.registrationForm.value.mobile;
    
    // Store pending registration data (photo will be uploaded after OTP verification)
    this.pendingRegistrationData = {
      name: this.registrationForm.value.name,
      mobile: this.registrationForm.value.mobile,
      password: this.registrationForm.value.password,
      email: this.registrationForm.value.email,
      gender: this.registrationForm.value.gender,
      currentWeight: this.registrationForm.value.currentWeight,
      currentHeight: this.registrationForm.value.currentHeight,
      dateOfBirth: new Date(this.registrationForm.value.dateOfBirth),
      fullAddress: this.registrationForm.value.fullAddress,
      altMobile: this.registrationForm.value.altMobile || undefined,
      recentPhoto: '',
      offlinePurchaseData: this.pendingOfflineData ? [...this.pendingOfflineData] : undefined
    };
    
    // Send OTP
    if (!this.recaptchaVerifier) {
      this.initializeRecaptcha();
    }
    
    if (!this.recaptchaVerifier) {
      this.isLoading = false;
      this.toastService.error('Failed to initialize reCAPTCHA. Please refresh the page.');
      return;
    }
    
    const otpSubscription = this.authService.sendOTP(mobile, this.recaptchaVerifier).subscribe({
      next: (result) => {
        this.isLoading = false;
        if (result.success) {
          this.confirmationResult = result.confirmationResult!;
          this.showOtpInput = true;
          this.otpSent = true;
          this.toastService.success(this.t('otpSent'));
          this.startCountdown();
          this.cdr.detectChanges();
        } else {
          this.toastService.error(result.message);
          this.cdr.detectChanges();
        }
      },
      error: (error) => {
        this.isLoading = false;
        this.handleOTPError(error);
        this.cdr.detectChanges();
      }
    });
    
    this.subscriptions.add(otpSubscription);
  }
  
  private handleOTPError(error: any): void {
    let errorMessage = 'Failed to send OTP. Please try again.';
    if (error?.code === 'auth/too-many-requests') {
      errorMessage = 'Too many OTP requests. Please wait 1-2 hours before trying again.';
    } else if (error?.code === 'auth/invalid-phone-number') {
      errorMessage = 'Invalid phone number. Please check and try again.';
    } else if (error?.code === 'auth/captcha-check-failed') {
      errorMessage = 'reCAPTCHA verification failed. Please complete it again.';
    } else if (error?.code === 'auth/invalid-app-credential') {
      errorMessage = 'Authentication error. Please refresh the page and try again.';
    }
    this.toastService.error(errorMessage);
  }
  
  // Verify OTP and complete registration
  private verifyOTPAndRegister(): void {
    if (!this.otpForm.valid) {
      this.toastService.error(this.t('validOtp'));
      return;
    }
    
    if (!this.pendingRegistrationData) {
      this.toastService.error(this.t('registrationDataNotFound'));
      this.showOtpInput = false;
      return;
    }
    
    this.isLoading = true;
    this.errorMessage = '';
    
    const otp = this.otpForm.value.otp;
    
    // Verify OTP first, then upload photo and register
    this.confirmationResult.confirm(otp).then(() => {
      const finishWithError = (msg: string) => {
        this.isLoading = false;
        this.isRegistering = false; // Reset flag on error
        this.toastService.error(msg);
      };

      const proceedToRegister = (photoUrl: string | null) => {
        this.isRegistering = true;
        const dataToRegister = {
          ...this.pendingRegistrationData!,
          recentPhoto: photoUrl || '',
          offlinePurchaseData: this.pendingRegistrationData!.offlinePurchaseData
        };
        const registerSub = this.authService.register(dataToRegister).subscribe(response => {
          this.isLoading = false;
          if (response.success) {
            this.toastService.success(this.t('registerSuccess'));
            setTimeout(() => {
              this.isRegistering = false;
              this.router.navigate(['/login']);
            }, 1200);
          } else {
            this.isRegistering = false;
            this.toastService.error(response.message);
            this.errorMessage = response.message;
          }
        });
        this.subscriptions.add(registerSub);
      };

      // Upload photo to storage first
      if (this.selectedPhotoFile) {
        const uploadSub = this.fileUploadService.uploadFile(this.selectedPhotoFile).subscribe(result => {
          if (result.success && result.downloadURL) {
            proceedToRegister(result.downloadURL);
          } else {
            finishWithError(result.error || 'Failed to upload photo. Please try again.');
          }
        });
        this.subscriptions.add(uploadSub);
      } else {
        proceedToRegister(null);
      }
    }).catch((error) => {
      this.isLoading = false;
      let message = 'Invalid OTP!';
      if (error?.code === 'auth/invalid-verification-code') {
        message = 'Invalid verification code!';
      } else if (error?.code === 'auth/code-expired') {
        message = 'OTP has expired. Please request a new one.';
      }
      this.toastService.error(message);
    });
  }

  private markFormGroupTouched() {
    Object.keys(this.registrationForm.controls).forEach(key => {
      const control = this.registrationForm.get(key);
      control?.markAsTouched();
    });
  }

  // Scroll to the first invalid field
  private scrollToFirstInvalidField(): void {
    const invalidFields = [
      'name', 'email', 'mobile', 'altMobile', 'password', 'confirmPassword',
      'gender', 'dateOfBirth', 'currentHeight', 'currentWeight', 'fullAddress', 'agreeTerms'
    ];

    for (const fieldName of invalidFields) {
      const control = this.registrationForm.get(fieldName);
      if (control && control.invalid && control.touched) {
        this.scrollToField(fieldName);
        break;
      }
    }
  }

  // Scroll to a specific field by name
  private scrollToField(fieldName: string): void {
    setTimeout(() => {
      const element = document.getElementById(fieldName);
      if (element) {
        element.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center',
          inline: 'nearest'
        });
        element.focus();
      }
    }, 100);
  }

  // Scroll to photo upload section
  private scrollToPhotoUpload(): void {
    setTimeout(() => {
      const element = document.querySelector('.file-upload-wrapper');
      if (element) {
        element.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center',
          inline: 'nearest'
        });
      }
    }, 100);
  }

  // Trigger a short animation on the card when validation fails
  private triggerValidationAnimation(): void {
    this.showValidationAnimation = false;
    this.cdr.detectChanges();
    this.showValidationAnimation = true;
    setTimeout(() => {
      this.showValidationAnimation = false;
      this.cdr.detectChanges();
    }, 400);
  }

  // Getter methods for easy access to form controls
  get name() { return this.registrationForm.get('name'); }
  get mobile() { return this.registrationForm.get('mobile'); }
  get password() { return this.registrationForm.get('password'); }
  get confirmPassword() { return this.registrationForm.get('confirmPassword'); }
  get email() { return this.registrationForm.get('email'); }
  get gender() { return this.registrationForm.get('gender'); }
  get currentWeight() { return this.registrationForm.get('currentWeight'); }
  get currentHeight() { return this.registrationForm.get('currentHeight'); }
  get dateOfBirth() { return this.registrationForm.get('dateOfBirth'); }
  get fullAddress() { return this.registrationForm.get('fullAddress'); }
  get altMobile() { return this.registrationForm.get('altMobile'); }
  get agreeTerms() { return this.registrationForm.get('agreeTerms'); }
  get alreadyBoughtDose() { return this.registrationForm.get('alreadyBoughtDose'); }
  get otpControl() { return this.otpForm.get('otp') as FormControl; }

  navigateToLogin() {
    this.router.navigate(['/login']);
  }
  
  // Initialize reCAPTCHA verifier
  private initializeRecaptcha(): void {
    try {
      // Check if reCAPTCHA container exists
      const container = document.getElementById('recaptcha-container');
      if (!container) {
        return;
      }

      // If container already has content, don't reinitialize
      if (container.children.length > 0) {
        return;
      }

      // Clear any existing verifier
      if (this.recaptchaVerifier) {
        try {
          this.recaptchaVerifier.clear();
        } catch (clearError) {
          // Silent cleanup
        }
      }

      // Create new verifier
      this.recaptchaVerifier = this.authService.createRecaptchaVerifier('recaptcha-container');
    } catch (error) {
      this.toastService.error('Failed to initialize reCAPTCHA. Please refresh the page.');
    }
  }
  
  // Start countdown for resend OTP
  private startCountdown(): void {
    this.countdown = 60;
    this.countdownInterval = setInterval(() => {
      this.countdown--;
      if (this.countdown <= 0) {
        clearInterval(this.countdownInterval);
      }
      this.cdr.detectChanges();
    }, 1000);
  }
  
  onOfflineModalConfirm(entries: OfflineDoseEntry[]): void {
    this.pendingOfflineData = entries;
    this.showOfflineModal = false;
    this.cdr.detectChanges();
    this.sendOTPForRegistration();
  }

  onOfflineModalCancel(): void {
    this.showOfflineModal = false;
    this.cdr.detectChanges();
  }

  // Go back to registration form
  goBack(): void {
    this.showOtpInput = false;
    this.otpSent = false;
    this.otpForm.reset();
    this.pendingRegistrationData = null;
    this.pendingOfflineData = null;
    this.successMessage = '';
    this.errorMessage = '';
    
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    
    // Clear reCAPTCHA so it can be reinitialized
    if (this.recaptchaVerifier) {
      try {
        this.recaptchaVerifier.clear();
      } catch (error) {
        // Silent cleanup
      }
    }
    
    // Clear the reCAPTCHA container and reinitialize
    const container = document.getElementById('recaptcha-container');
    if (container) {
      container.innerHTML = '';
      setTimeout(() => this.initializeRecaptcha(), 100);
    }
  }

  // Open terms and conditions modal when checkbox is clicked
  openTermsModal(): void {
    // Use Bootstrap's native modal trigger
    const modalElement = document.getElementById('termsModal');
    if (modalElement) {
      // Trigger the modal using Bootstrap's data attributes
      const modalTrigger = document.createElement('button');
      modalTrigger.setAttribute('data-bs-toggle', 'modal');
      modalTrigger.setAttribute('data-bs-target', '#termsModal');
      modalTrigger.style.display = 'none';
      document.body.appendChild(modalTrigger);
      
      // Trigger the modal
      modalTrigger.click();
      
      // Clean up
      document.body.removeChild(modalTrigger);
    }
  }

  // Open date picker when input is focused (works on web and mobile)
  openDatePicker(): void {
    const dateInput = document.getElementById('dateOfBirth') as HTMLInputElement;
    if (dateInput) {
      // Use setTimeout to ensure the focus event completes first
      setTimeout(() => {
        // For modern browsers, use showPicker API
        if ('showPicker' in dateInput && typeof (dateInput as any).showPicker === 'function') {
          try {
            (dateInput as any).showPicker();
          } catch (error) {
            // Fallback: trigger click if showPicker fails
            dateInput.click();
          }
        } else {
          // Fallback for older browsers: trigger click
          dateInput.click();
        }
      }, 10);
    }
  }

  // Translation method
  t(key: string, params?: { [key: string]: string | number }): string {
    return this.languageService.t(key, params);
  }
}
